import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/Icon'
import { PageHeader, StatTiles } from '@/components/PageHeader'
import { fetchClients } from '@/features/clients/api'
import { ClientPanel } from '@/features/clients/ClientPanel'
import { NewClientDialog } from '@/features/clients/NewClientDialog'
import type { ApiError } from '@/lib/api/client'
import type { Client, ClientHealth } from '@/types/support'

const HEALTH_CLASS: Record<ClientHealth, string> = {
  good: 'border-[color:var(--color-state-done)] text-[color:var(--color-state-done)]',
  watch: 'border-brand-300 bg-brand-50 text-brand-700',
  at_risk:
    'border-[color:var(--color-state-blocked)] text-[color:var(--color-state-blocked)]',
}

const HEALTH_KEY: Record<ClientHealth, string> = {
  good: 'clients.healthGood',
  watch: 'clients.healthWatch',
  at_risk: 'clients.healthAtRisk',
}

/**
 * The relationships behind the portfolio.
 *
 * Client was the entity the workspace file was missing — 41 of them existed
 * only as a line of text inside a project description. This is the screen that
 * makes them a thing you can look at.
 */
export function ClientsPage() {
  const { t } = useTranslation()
  const [creating, setCreating] = useState(false)
  const [open, setOpen] = useState<string | null>(null)

  const clients = useQuery<Client[], ApiError>({
    queryKey: ['clients'],
    queryFn: fetchClients,
    retry: false,
  })

  if (clients.isPending) {
    return <p className="text-ink-500 font-mono text-xs">{t('common.loading')}</p>
  }

  const rows = clients.data ?? []
  const atRisk = rows.filter((client) => client.health === 'at_risk').length

  return (
    <div>
      <PageHeader
        title={t('clients.title')}
        subtitle={t('clients.subtitle')}
        action={
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="bg-brand-600 flex items-center gap-1.5 rounded px-3 py-1.5 text-[13px] font-medium text-white"
          >
            <Icon name="plus" className="size-3.5" />
            {t('clients.newClient')}
          </button>
        }
      />

      <StatTiles
        items={[
          { label: t('clients.title'), value: rows.length },
          { label: t('clients.projects'), value: rows.reduce((n, c) => n + c.projectCount, 0) },
          { label: t('clients.healthAtRisk'), value: atRisk },
        ]}
      />

      {rows.length === 0 ? (
        <p className="border-line bg-surface text-ink-700 rounded-md border p-4 text-sm">
          {t('clients.none')}
        </p>
      ) : (
        <div className="border-line bg-surface overflow-x-auto rounded-md border">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-line-strong text-ink-500 border-b font-mono text-[10px] tracking-widest uppercase">
                <th className="px-4 py-2.5 text-start font-medium">{t('clients.client')}</th>
                <th className="px-4 py-2.5 text-start font-medium">{t('clients.sector')}</th>
                <th className="px-4 py-2.5 text-start font-medium">{t('clients.contact')}</th>
                <th className="px-4 py-2.5 text-end font-medium">{t('clients.projects')}</th>
                <th className="px-4 py-2.5 text-start font-medium">{t('clients.health')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((client) => (
                <tr key={client.id} className="border-line hover:bg-surface-2 border-b last:border-0">
                  <td className="px-4 py-2.5">
                    <button
                      type="button"
                      onClick={() => setOpen(client.slug)}
                      className="text-ink-900 text-start"
                    >
                      {client.name}
                    </button>
                  </td>
                  <td className="text-ink-700 px-4 py-2.5">{client.sector ?? '—'}</td>
                  <td className="text-ink-700 px-4 py-2.5">
                    {client.primaryContact.email === null ? (
                      '—'
                    ) : (
                      <a
                        href={`mailto:${client.primaryContact.email}`}
                        className="text-brand-600 underline"
                      >
                        {client.primaryContact.name ?? client.primaryContact.email}
                      </a>
                    )}
                  </td>
                  <td className="text-ink-700 px-4 py-2.5 text-end tabular-nums">
                    {client.projectCount}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      title={client.healthReason ?? undefined}
                      className={`rounded border px-2 py-0.5 font-mono text-xs ${HEALTH_CLASS[client.health]}`}
                    >
                      {t(HEALTH_KEY[client.health])}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating ? <NewClientDialog onClose={() => setCreating(false)} /> : null}
      {open === null ? null : (
        <ClientPanel key={open} slug={open} onClose={() => setOpen(null)} />
      )}
    </div>
  )
}
